<!--
  - partner-map.vue
  - @author Martin Appelmann <martin@movearound.fit>
  - @copyright 2022 movearound GmbH
  -->

<template>
    <div class="row pt-5 m-2 justify-content-center">
        <div class="partner-map-container">
            <div class="row pb-5 align-items-end">
                <div class="col">
                    <div class="map-title">Unsere Standorte</div>
                </div>
            </div>
            <div v-if="!loading && placeCounter > 0" class="row pb-3">
                <div class="col">
                    <div class="location-info text-white font-weight-light">
                        {{
                            placeCounter === 1 ?
                                (placeCounter + ' Standort in ' + currentLocation + ' gefunden') :
                                (placeCounter + ' Standorte in ' + currentLocation + ' gefunden')
                        }}
                    </div>
                </div>
            </div>
            <div class="row pb-4">
                <div class="col-12 col-md-9">
                    <div class="row">
                        <div class="col-12 col-md-8 align-self-center">
                            <div class="form-group my-2">
                                <input
                                    id="city"
                                    v-model="city"
                                    class="form-control search-input"
                                    placeholder="Stadt eintragen"
                                    required
                                    v-on:keyup.enter="search"
                                />
                                <small>
                                    {{ msgCity }}
                                </small>
                            </div>
                        </div>
                        <div class="col-12 col-md-4">
                            <button
                                class="btn btn-search my-2"
                                v-on:click="search"
                                :disabled="loading"
                            >
                                <span v-if="loading" class="spinner-border spinner-border-sm" role="status"
                                      aria-hidden="true"></span>
                                suchen
                            </button>
                        </div>
                        <div class="col-12 col-md-4">
                            <div class="form-group my-2">
                                <multiselect
                                    v-model="selectedCategories"
                                    :options="categories"
                                    mode="multiple"
                                    :searchable="true"
                                    :close-on-select="false"
                                    :show-labels="false"
                                    :multipleLabel="setMultipleLabel"
                                    :disabled="isEmpty"
                                    :hide-selected="false"
                                    :block-keys="['Tab', 'Enter']"
                                    placeholder="nach Kategory filtern"
                                    @change="onChangeCategory"
                                ></multiselect>
                            </div>
                        </div>
                        <div class="col-12 col-md-4">
                            <div class="form-group my-2 mb-md-0">
                                <multiselect
                                    v-model="radius"
                                    :canClear="false"
                                    :options="radiusSelections"
                                    @input="onRadiusChange"
                                    :allow-empty="false"
                                    :show-labels="false"
                                    :block-keys="['Tab', 'Enter']"
                                    placeholder="Umkreis auswählen"
                                    label="text"
                                ></multiselect>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-3 align-self-end">
                    <div class="text-right">
                        <label class="mt-3 mb-0 mt-md-0 text-white">Karten anzeigen</label>
                        <input
                            type="checkbox"
                            v-model="showMap"
                            class="ml-2 mt-3 mt-md-0"
                            @change="clickedCheckbox($event)"
                        />
                    </div>
                </div>
            </div>
            <div class="row mb-5">
                <div id="scroll-container" class="scroll-container"
                     :class="showMap ? 'col-md-6' : 'col-12'"
                >
                    <div class="row">
                        <div v-if="!loading && filteredPlaces != null && filteredPlaces.length <= 0"
                             class="m-5 text-center col">
                            Keine Standorte {{ currentLocation === '' ? '' : 'in ' + currentLocation }} gefunden.
                        </div>
                        <div v-for="place in filteredPlaces"
                             :key="place.id"
                             :class="showMap ? 'col-lg-6' : 'col-lg-3'"
                             class="grid-margin stretch-card"
                        >
                            <div class="card partner-card pe-auto" :id="place.id" v-on:click="loadDetails(place)">
                                <a target="_blank" class="partner-card-link" :href="'partner-map/place/'+place.id">
                                    <div class="image-holder">
                                        <img class="card-img-top partner-card-image"
                                             :src="place.first_image_url" alt="">
                                    </div>
                                    <div class="name-overlay">
                                        <div class="card-partner-name m-4">
                                            <img v-for="category in place.filtered_categories" class="mr-3 h-100"
                                                 :src="'/assets/images/categories/' + category.title + '.svg'">
                                        </div>
                                    </div>
                                    <div class="card-body p-4">
                                        <div v-if="place.website" class="stretched-link"></div>
                                        <h4 class="card-categories">{{ place.name }}</h4>
                                        <div class="py-3 card-info">
                                            Öffnungszeiten: {{ place.today_opening_hour }}
                                        </div>
                                        <div class="card-info">{{ place.address.street }}</div>
                                        <div class="card-info">
                                            {{ place.address.postal_code + ', ' + place.address.city }}
                                        </div>
                                    </div>

                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="showMap" class="col-12 col-md-6 pb-4 pb-md-0 order-first order-md-last">
                    <mapbox-map
                        :accessToken="accessToken"
                        :mapStyle="mapStyle"
                        @loaded="mapLoaded"
                        :center="coordinates"
                        :zoom="12"
                    >
                        <mapbox-marker
                            v-for="place in filteredPlaces"
                            :key="place.id"
                            :lngLat="[ place.address.lng, place.address.lat ]"
                        >
                            <template v-slot:icon>
                                <img @click="markerClick(place.id)" slot="marker" class="place-marker" alt=""
                                     :src="'assets/images/pins/pin_active' +
                                 (place.first_category === '' ? '_blank' : '_') +
                                 place.first_category + '.png'"
                                />
                            </template>

                            <mapbox-popup
                                :closeButton="true"
                            >
                                <div class="m-3">
                                    <h3 class="popup-title">{{ place.name }}</h3>
                                    <h4 class="card-categories">{{ place.translate_categories }}</h4>
                                    <div class="py-3 card-info">Öffnungszeiten: {{ place.today_opening_hour }}</div>
                                    <div class="card-info">{{ place.address.street }},</div>
                                    <div class="card-info">
                                        {{ place.address.postal_code }} {{ place.address.city }}
                                    </div>
                                </div>
                            </mapbox-popup>
                        </mapbox-marker>
                    </mapbox-map>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Multiselect from '@vueform/multiselect'
import { MapboxMap } from 'vue-mapbox-ts'

let map = null

export default {
    name: 'partner-map',
    components: {
        Multiselect,
        MapboxMap
    },
    data: function () {
        return {
            coordinates: [10.516667, 52.266666],
            city: 'Braunschweig',
            currentLocation: '',
            radius: 50,
            placeCounter: 0,
            radiusSelections: [
                { value: 20, text: '20 km' },
                { value: 50, text: '50 km' },
                { value: 100, text: '100 km' }
            ],
            places: [],
            filteredPlaces: [],
            activeMarker: null,
            loading: false,
            showMap: false,
            errorCity: null,
            msgCity: '',
            accessToken: process.env.MAPBOX_TOKEN,
            mapStyle: process.env.MAPBOX_DESIGN_TOKEN,
            categories: [],
            selectedCategories: [],
        }
    },
    created () {
        this.search()
        this.getCategories()
    },
    computed: {
        isEmpty () {
            return this.categories.length <= 0
        }
    },
    methods: {
        clickedCheckbox(event) {
            if (map != null) {
                map.resize();
            }
        },
        setMultipleLabel (values) {
            return values.length + ' Kategorien ausgewählt'
        },
        loadDetails (place) {
            console.log(place)
        },
        mapLoaded (event) {
            map = event
            map.resize()
            map.flyTo({
                center: this.coordinates,
                zoom: 13,
                speed: 2
            })
        },
        getCategoryIcon (name) {
            switch (name) {
                case 'bouldering':
                    return bouldern
                case 'yoga':
                    return yoga_wei_
                case 'gym':
                    return fitness
                case 'ems':
                    return ems
                case 'fighting-sport':
                    return kampfsport
                case 'functional-training':
                    return functional_training
                case 'dance':
                    return tanzen
                case 'swimming':
                    return schwimmen
                case 'court-sport':
                    return tennis
            }
            return Icons.add
        },
        markerClick (id) {
            if (this.activeMarker != null) {
                this.activeMarker.classList.remove('partner-card--highlight')
            }

            var scrollContainer = document.getElementById('scroll-container')
            var markerElement = document.getElementById(id)

            markerElement.classList.add('partner-card--highlight')
            this.activeMarker = markerElement
            var elementRect = markerElement.getBoundingClientRect()
            var scrollContainerRect = scrollContainer.getBoundingClientRect()

            scrollContainer.scrollTo({
                behavior: 'smooth',
                left: 0,
                top: scrollContainer.scrollTop + elementRect.top - scrollContainerRect.top - (scrollContainerRect.height / 2 - elementRect.height / 2)
            })
        },
        onRadiusChange (event) {
            this.radius = event
            this.search()
        },
        onChangeCategory (event) {
            this.selectedCategories = event
            if (this.selectedCategories.length !== 0) {
                this.filteredPlaces = this.places.filter((place) => {
                    var categoriesPlaceArray = place.categories.map(function (item) {
                        return item['name']
                    })
                    return this.selectedCategories.some(i => categoriesPlaceArray.includes(i))
                })
            } else {
                this.filteredPlaces = this.places
            }
        },
        search () {
            this.loading = true
            axios.get('api/place', {
                params: {
                    city: this.city,
                    distance: this.radius
                }
            }).then(async (response) => {
                this.loading = false
                this.errorCity = null
                this.places = response.data.data
                this.filteredPlaces = this.places
                this.currentLocation = response.data.location
                if (this.filteredPlaces !== null) {
                    this.placeCounter = this.filteredPlaces.length
                }
                this.coordinates = [response.data.lng, response.data.lat]
                if (map !== null) {
                    map.flyTo({
                        center: [response.data.lng, response.data.lat],
                        zoom: 13,
                        speed: 2
                    })
                }
            }, (error) => {
                this.loading = false
                if (error.response.status === 422) {
                    if (error.response.data.errors.city != null) {
                        this.errorCity = false
                        this.msgCity = error.response.data.errors.city[0]
                    }
                }
            })
        },
        getCategories () {
            this.loading = true
            axios.get('v3/app/place/categories').then(async (response) => {
                this.categories = response.data.data
                var filteredResult = response.data.data.filter(category => category.type === 0)
                var categoriesArray = []
                filteredResult.forEach(function (element) {
                    categoriesArray.push(element.name)
                })
                this.categories = categoriesArray
            })
        }
    }
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped lang="scss">
.mapboxgl-popup-content {
    padding: 1rem !important;
}

.search-input {
    background-color: transparent !important;
    border-color: #fe4f33 !important;
    border-width: 3px !important;
    border-radius: 10px;
    box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.1) !important;
    font-family: Resolve-Sans, sans-serif !important;
    letter-spacing: 1px !important;
    color: #ecf0f4;

    & ::placeholder {
        font-family: Resolve-Sans, sans-serif;
        letter-spacing: 1px;
        color: #707070;
    }

    & :focus {
        font-family: Resolve-Sans, sans-serif;
        letter-spacing: 1px;
        background-color: transparent !important;
        color: black;
    }

}

.radius-input {
    background-color: #ecf0f4;
    border-radius: 10px;
    font-family: Resolve-Sans, sans-serif !important;
    letter-spacing: 1px !important;
    height: 2.875rem;
}

.name-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 1.5rem;
    height: 15rem;

    @media (min-width: 800px) {
        height: 25rem;
    }

    @media (min-width: 1200px) {
        height: 15rem;
    }
}

.partner-card-image {
    border-radius: 0;
    height: 15rem;
    object-fit: cover;

    @media (min-width: 800px) {
        height: 25rem;
    }

    @media (min-width: 1200px) {
        height: 15rem;
    }
}

.card-categories {
    color: black;
}

.partner-card-link:hover {
    text-decoration: none;
}

.card-partner-name {
    font-family: Resolve-Sans, sans-serif;
    letter-spacing: 1px;
    position: absolute;
    height: 1.5rem;
    bottom: 0;
    left: 0;
    color: #fe4f33 !important;

    filter: invert(46%) sepia(31%) saturate(6766%) hue-rotate(341deg) brightness(100%) contrast(96%);
}

.partner-card--highlight {
    background-color: #f6f6f6;
    box-shadow: 10px 10px 14px rgba(0, 0, 0, 0.3);
}

.btn-search {
    font-family: Resolve-Sans, sans-serif !important;
    letter-spacing: 3px;
    text-shadow: 3px 3px 7px rgba(0, 0, 0, 0.3) !important;
    color: #1a1a1a;
    background-color: #c2d3dc;
    border: none;
    border-radius: 50px !important;
    box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.3) !important;
    padding: 10px !important;
    text-transform: capitalize !important;
    display: block;
    width: 100%;
    height: 2.875rem;

    @media (min-width: 1200px) {
        padding: 12px 40px !important;
    }

    &:hover {
        background-color: #859fb5 !important;
        color: #1a1a1a !important;
    }
}

.location-info {
    font-size: 1rem;
    @media (min-width: 1200px) {
        font-size: 1.5rem;
    }
}

.place-marker {
    height: 6vh;
}
</style>
